<template>
  <component
    :is="withManagement ? 'span' : 'router-link'"
    v-bind="withManagement ? null : { to: { path: `/article/${article.slug}` } }"
    class="idf-article-thumbnail"
    :class="{ 'idf-article-thumbnail--mini': miniMode, 'idf-article-thumbnail--management': withManagement }"
  >
    <adn-image
      :max-width-to-autosize="264"
      :source="article.header && (article.header.image || article.header.imageUrl)"
      :alt="(article.header && article.header.imageAlt) || 'Image de l\'article non trouvé'"
      class="idf-article-thumbnail--header-image"
      data-test-article-thumbnail-image
    />
    <div class="idf-article-thumbnail--content">
      <ul class="idf-article-thumbnail__tags-list">
        <template v-if="tagStrings.length">
          <li v-for="(tag, index) in tagStrings" :key="index" class="idf-article-thumbnail__tag-item" data-test-tag>
            {{ tag }}
          </li>
        </template>
        <li v-else-if="useSpacer" class="idf-article-thumbnail__tag-item idf-article-thumbnail__tag-item--spacer"></li>
      </ul>
      <div :title="article.title" class="idf-article-thumbnail--title">{{ truncatedTitle }}</div>
      <div v-if="display.summary" class="idf-article-thumbnail--summary">{{ article.summary }}</div>
    </div>
    <div class="idf-article-thumbnail_footer">
      <template v-if="display.author">
        <div v-if="article.header && article.header.author" class="idf-article-thumbnail--author">
          PAR {{ article.header.author.toUpperCase() }}
        </div>
        <div v-else class="idf-article-thumbnail--author"></div>
      </template>
      <div v-else-if="article.header && display.date" class="idf-article-thumbnail--published_at">
        {{ formatDate(article.header.publishedAt, 'd LLL yyyy') }}
      </div>
      <span v-if="display.arrow" class="adn-icon adn-icon--navigation-arrow-right" aria-hidden="true" />
    </div>
    <div v-if="display.management" class="idf-article-thumbnail_footer-management">
      <hr />
      <slot name="management"></slot>
    </div>
  </component>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, type Ref } from 'vue'
import AdnImage from '../AdnImage/AdnImage.vue'

export default defineComponent({
  name: 'IdfArticleThumbnail',
  components: { AdnImage },
  props: {
    truncateTitle: { type: Number, default: -1 },
    article: {
      type: Object as PropType<any>,
      default: () => ({
        slug: '',
        title: '',
        summary: '',
        header: { author: '', image: null, imageAlt: '', richBoxTag: '', imageUrl: '', publishedAt: '' },
      }),
    },
    miniMode: { type: Boolean, default: false },
    useSpacer: { type: Boolean, default: false },
    withManagement: { type: Boolean, default: false },
    formatDate: {
      type: Function,
      default: (date: Date) => {
        if (!date) return undefined
        return new Date(date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' })
      },
    },
  },
  setup(props) {
    const display = computed(() => {
      const displayNormal = {
        summary: true,
        author: false,
        date: !props.withManagement,
        arrow: !props.withManagement,
        management: props.withManagement,
      }

      const displayMiniMode = {
        summary: false,
        author: true,
        date: false,
        arrow: !props.withManagement,
        management: props.withManagement,
      }

      if (props.miniMode) {
        return displayMiniMode
      }

      return displayNormal
    })

    const truncateText = (text, maxCharacters) => {
      if (!text || text.length === 0 || text.length < maxCharacters) {
        return text
      }

      const parts = text.split(' ')
      let concatenated = text
      while (parts.length > 1 && concatenated.length > maxCharacters) {
        parts.pop()
        concatenated = parts.join(' ')
      }

      return `${concatenated} ...`
    }

    const truncatedTitle = computed(() => {
      return props.truncateTitle > 0 ? truncateText(props.article.title, props.truncateTitle) : props.article.title
    })

    const tagStrings: Ref<string[]> = computed(() => {
      return (
        props.article.header?.richBoxTag
          ?.split(/[;,]/)
          .map((tag) => tag.trim())
          .filter(Boolean) || ['']
      )
    })

    return { truncatedTitle, truncateText, tagStrings, display }
  },
})
</script>

<style lang="scss">
.idf-article-thumbnail--header-image {
  img {
    border-radius: var(--article-img-border-radius, 0.8rem);
    object-fit: cover;
    object-position: center;
  }

  height: 12rem;

  @media (max-width: $breakpoint-md) {
    height: 8rem;
  }

  flex: 0 0 auto;
}

.idf-article-thumbnail--content {
  color: var(--color-black);
  padding: 0 var(--spacing-1) 0 0;
  flex: 1 1 auto;
}

.idf-article-thumbnail--title {
  font-weight: bold;
  max-height: 10rem;
  overflow: hidden;
}

.idf-article-thumbnail--summary {
  @media (max-width: $breakpoint-md) {
    display: none;
  }
}

.idf-article-thumbnail--published_at,
.idf-article-thumbnail--author {
  color: var(--color-grey-dark);
  font-size: 1.2rem;
  flex: 0 0 auto;
  padding: 0;
}

.idf-article-thumbnail {
  margin: 0 var(--spacing-2);
  border: 1px solid var(--color-white);
  box-shadow: -0.2rem 0.2rem var(--spacing-2) rgb(87 94 98 / 20%);
  display: flex;
  background-color: var(--color-white);
  flex-direction: column;
  padding: var(--spacing-2);
  border-radius: var(--article-border-radius, 1.6rem);
  gap: var(--spacing-1);
  height: var(--article-lg-height, 36rem);
  width: var(--article-lg-width, 28rem);

  @media (max-width: $breakpoint-md) {
    height: var(--article-sm-height, 26rem);
    width: var(--article-sm-width, 20rem);
  }

  &:not(.idf-article-thumbnail--management):hover {
    border-color: var(--color-blue);
    box-shadow: var(--shadow)-dark;
  }
}

.idf-article-thumbnail_footer {
  display: flex;
  flex-wrap: nowrap;
  margin: 0;
  justify-content: space-between;
  overflow-x: auto;

  @media all and (min-width: $breakpoint-lg) {
    flex-wrap: wrap;
    overflow-x: initial;
  }
}

.idf-article-thumbnail_footer-management {
  margin-top: var(--spacing-3);
}

.idf-article-thumbnail__tags-list {
  display: flex;
  gap: var(--tagsGap, var(--spacing-2, 0.8rem)) !important;
  padding: var(--article-tags-padding, 0) !important;
  overflow-x: auto;
}

.idf-article-thumbnail__tag-item,
.idf-article-thumbnail__tag-item--spacer::after {
  display: inline-block;
  font-size: var(--tagFontSize, var(--font-size-xs, 1.2rem)) !important;
  line-height: var(--tagLineHeight, var(--line-height-xs, 2rem)) !important;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 4%;
  padding: var(--tagPadding, var(--spacing-1, 0.4rem) 0) !important;
  color: var(--tagTextColor, var(--color-main, #0076b4)) !important;
  margin: var(--tagMargin, 0) !important;
}

.idf-article-thumbnail__tag-item--spacer {
  background-color: transparent;

  &::after {
    content: '';
  }
}
</style>
